/**
* @file 系统管理-菜单管理
* @description 支持菜单的新增、编辑、删除及同步到所有机构的操作
**/
<template>
  <div id="menu-manage">
    <!-- 工具栏 -->
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-plus" size="small" @click="addMenu(null)">新增一级菜单</el-button>
      <el-button icon="el-icon-refresh" size="small" @click="loadMenuList" :loading="loading">刷新</el-button>
      <el-alert
        type="info"
        :closable="false"
        style="display:inline-flex; margin-left:12px; padding:4px 12px; width:auto;"
        show-icon>
        <span slot="title">新增菜单后请点击「同步到所有机构」，使菜单对各租户可见</span>
      </el-alert>
    </div>

    <!-- 菜单树表格 -->
    <el-table
      v-loading="loading"
      :data="menuTree"
      row-key="id"
      :tree-props="{ children: 'children' }"
      default-expand-all
      border
      style="width: 100%; margin-top: 14px;">
      <el-table-column prop="menuName" label="菜单名称" width="140" show-overflow-tooltip>
        <template slot-scope="{row}">
          <i :class="row.icon || 'el-icon-menu'" style="margin-right:6px;color:#409EFF"></i>
          <span>{{ row.menuName }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="id" label="菜单ID" width="80" align="center" show-overflow-tooltip></el-table-column>
      <el-table-column prop="parentId" label="父级ID" width="80" align="center" show-overflow-tooltip>
        <template slot-scope="{row}">
          <span>{{ row.parentId === 0 ? '—' : row.parentId }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="permission" label="权限标识" width="160" show-overflow-tooltip></el-table-column>
      <el-table-column prop="path" label="路由路径" width="160" show-overflow-tooltip></el-table-column>
      <el-table-column prop="sort" label="排序" width="70" align="center"></el-table-column>
      <el-table-column label="平台适用" width="200" align="center" show-overflow-tooltip>
        <template slot-scope="{row}">
          <el-tag v-if="row.isDzky === 1" size="mini" type="success" style="margin:2px">DZKY</el-tag>
          <el-tag v-if="row.isKyz === 1" size="mini" type="warning" style="margin:2px">KYZ</el-tag>
          <el-tag v-if="row.isSfc === 1" size="mini" type="danger" style="margin:2px">SFC</el-tag>
          <el-tag v-if="row.isCz === 1" size="mini" style="margin:2px">CZ</el-tag>
        </template>
      </el-table-column>
      <!-- 使用 min-width 使该列吸收剩余宽度，避免「操作」列 fixed 时右侧出现空白列 -->
      <el-table-column label="分公司禁用" min-width="80" align="center" show-overflow-tooltip>
        <template slot-scope="{row}">
          <el-tag :type="row.isDisabled === 1 ? 'danger' : 'success'" size="mini">
            {{ row.isDisabled === 1 ? '禁用' : '可用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="260" fixed="right">
        <template slot-scope="{row}">
          <el-tooltip content="新增子菜单" placement="top">
            <el-button type="text" icon="el-icon-plus" @click="addMenu(row)">子菜单</el-button>
          </el-tooltip>
          <el-tooltip content="编辑" placement="top">
            <el-button type="text" icon="el-icon-edit" @click="editMenu(row)">编辑</el-button>
          </el-tooltip>
          <el-tooltip content="同步到所有机构" placement="top">
            <el-button type="text" icon="el-icon-refresh-right" @click="syncMenu(row)">同步</el-button>
          </el-tooltip>
          <el-tooltip content="删除（需先删除子菜单）" placement="top">
            <el-button type="text" icon="el-icon-delete" style="color:#F56C6C" @click="deleteMenu(row)">删除</el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      :close-on-click-modal="false"
      @close="resetForm">
      <el-form :model="form" :rules="rules" ref="menuForm" label-width="110px">
        <el-form-item label="父级菜单">
          <el-input :value="parentName" disabled placeholder="一级菜单（无父级）"></el-input>
        </el-form-item>
        <el-form-item label="菜单名称" prop="menuName">
          <el-input v-model="form.menuName" placeholder="如：订单管理" maxlength="30"></el-input>
        </el-form-item>
        <el-form-item label="权限标识" prop="permission">
          <el-input v-model="form.permission" placeholder="如：orderManage（英文）"></el-input>
        </el-form-item>
        <el-form-item label="图标" prop="icon">
          <el-input v-model="form.icon" placeholder="如：el-icon-s-order">
            <template slot="prepend">
              <i :class="form.icon || 'el-icon-menu'" style="font-size:16px"></i>
            </template>
          </el-input>
          <div style="color:#909399;font-size:12px;margin-top:4px">
            使用 Element UI 图标，如 el-icon-s-order，参考
            <a href="https://element.eleme.cn/#/zh-CN/component/icon" target="_blank">图标列表</a>
          </div>
        </el-form-item>
        <el-form-item label="路由路径" prop="path">
          <el-input v-model="form.path" placeholder="如：/orderManage"></el-input>
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="form.sort" :min="0" :max="999" style="width:120px"></el-input-number>
          <span style="color:#909399;font-size:12px;margin-left:8px">数字越小越靠前</span>
        </el-form-item>
        <el-form-item label="分公司禁用">
          <el-radio-group v-model="form.isDisabled">
            <el-radio :label="2">可用（默认）</el-radio>
            <el-radio :label="1">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="适用平台">
          <div style="display:flex; gap:20px; flex-wrap:wrap;">
            <el-checkbox v-model="form.isDzkyBool" label="DZKY（专线定制）"></el-checkbox>
            <el-checkbox v-model="form.isKyzBool" label="KYZ（客运站）"></el-checkbox>
            <el-checkbox v-model="form.isSfcBool" label="SFC（顺风车）"></el-checkbox>
            <el-checkbox v-model="form.isCzBool" label="CZ（场站）"></el-checkbox>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'menuManage',
  data() {
    return {
      loading: false,
      saving: false,
      // 平铺菜单列表
      flatMenuList: [],
      // 树形菜单列表（用于表格展示）
      menuTree: [],
      // 弹窗
      dialogVisible: false,
      dialogTitle: '新增菜单',
      // 表单
      form: {
        id: null,
        parentId: 0,
        menuName: '',
        permission: '',
        icon: '',
        path: '',
        sort: 1,
        isDisabled: 2,
        isDzkyBool: true,
        isKyzBool: false,
        isSfcBool: false,
        isCzBool: false,
      },
      // 父级菜单名称
      parentName: '无（一级菜单）',
      // 表单校验
      rules: {
        menuName: [
          { required: true, message: '请输入菜单名称', trigger: 'blur' },
          { min: 1, max: 20, message: '长度1-20个字符', trigger: 'blur' }
        ],
        sort: [
          { required: true, message: '请输入排序', trigger: 'blur' }
        ],
      }
    }
  },

  created() {
    this.loadMenuList()
  },

  methods: {
    /**
     * 加载所有菜单列表
     */
    async loadMenuList() {
      this.loading = true
      try {
        const res = await this.$axios.get('/api/v1/manage/menu/all')
        if (res && res.code === 1) {
          this.flatMenuList = res.data || []
          this.menuTree = this.buildTree(this.flatMenuList)
        }
      } catch (e) {
        this.$message.error('菜单加载失败')
      } finally {
        this.loading = false
      }
    },

    /**
     * 构建菜单树
     */
    buildTree(flatList) {
      const map = {}
      flatList.forEach(item => {
        map[item.id] = { ...item, children: [] }
      })
      const tree = []
      flatList.forEach(item => {
        if (item.parentId === 0) {
          tree.push(map[item.id])
        } else if (map[item.parentId]) {
          map[item.parentId].children.push(map[item.id])
        }
      })
      // 排序
      const sortFn = (arr) => {
        arr.sort((a, b) => (a.sort || 0) - (b.sort || 0))
        arr.forEach(item => {
          if (item.children && item.children.length > 0) {
            sortFn(item.children)
          }
        })
      }
      sortFn(tree)
      return tree
    },

    /**
     * 新增菜单
     * @param parentRow 父级菜单行（null表示一级菜单）
     */
    addMenu(parentRow) {
      this.dialogTitle = parentRow ? `新增子菜单（父级：${parentRow.menuName}）` : '新增一级菜单'
      this.form = {
        id: null,
        parentId: parentRow ? parentRow.id : 0,
        menuName: '',
        permission: '',
        icon: '',
        path: '',
        sort: 1,
        isDisabled: 2,
        isDzkyBool: true,
        isKyzBool: false,
        isSfcBool: false,
        isCzBool: false,
      }
      this.parentName = parentRow ? parentRow.menuName : '无（一级菜单）'
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.menuForm && this.$refs.menuForm.clearValidate()
      })
    },

    /**
     * 编辑菜单
     */
    editMenu(row) {
      this.dialogTitle = `编辑菜单：${row.menuName}`
      const parent = this.flatMenuList.find(m => m.id === row.parentId)
      this.parentName = parent ? parent.menuName : '无（一级菜单）'
      this.form = {
        id: row.id,
        parentId: row.parentId,
        menuName: row.menuName,
        permission: row.permission || '',
        icon: row.icon || '',
        path: row.path || '',
        sort: row.sort || 1,
        isDisabled: row.isDisabled || 2,
        isDzkyBool: row.isDzky === 1,
        isKyzBool: row.isKyz === 1,
        isSfcBool: row.isSfc === 1,
        isCzBool: row.isCz === 1,
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.menuForm && this.$refs.menuForm.clearValidate()
      })
    },

    /**
     * 同步菜单到所有机构
     */
    async syncMenu(row) {
      try {
        await this.$confirm(
          `确认将菜单「${row.menuName}」同步到所有总机构和超级管理员角色吗？`,
          '同步确认',
          { type: 'warning' }
        )
        const res = await this.$axios.post(`/api/v1/manage/menu/manage/sync/${row.id}`)
        if (res && res.code === 1) {
          this.$message.success(res.data || '同步成功')
        } else {
          this.$message.error(res.msg || '同步失败')
        }
      } catch (e) {
        // 取消操作
      }
    },

    /**
     * 删除菜单
     */
    async deleteMenu(row) {
      try {
        await this.$confirm(
          `确认删除菜单「${row.menuName}」？删除后将同步清除所有机构和角色中该菜单的权限。注意：需先删除子菜单。`,
          '删除确认',
          { type: 'error', confirmButtonText: '确认删除', confirmButtonClass: 'el-button--danger' }
        )
        const res = await this.$axios.delete(`/api/v1/manage/menu/manage/${row.id}`)
        if (res && res.code === 1) {
          this.$message.success('删除成功')
          this.loadMenuList()
          this.$bus.$emit('menu-updated')
        } else {
          this.$message.error(res.msg || '删除失败')
        }
      } catch (e) {
        // 取消操作
      }
    },

    /**
     * 提交表单
     */
    async submitForm() {
      this.$refs.menuForm.validate(async (valid) => {
        if (!valid) return
        this.saving = true
        try {
          const params = {
            parentId: this.form.parentId,
            menuName: this.form.menuName,
            permission: this.form.permission,
            icon: this.form.icon,
            path: this.form.path,
            sort: this.form.sort,
            isDisabled: this.form.isDisabled,
            isDzky: this.form.isDzkyBool ? 1 : 0,
            isKyz: this.form.isKyzBool ? 1 : 0,
            isSfc: this.form.isSfcBool ? 1 : 0,
            isCz: this.form.isCzBool ? 1 : 0,
          }
          let res
          if (this.form.id) {
            // 编辑
            res = await this.$axios.put('/api/v1/manage/menu/manage', { ...params, id: this.form.id })
          } else {
            // 新增
            res = await this.$axios.post('/api/v1/manage/menu/manage', params)
          }
          if (res && res.code === 1) {
            this.$message.success(this.form.id ? '修改成功' : `新增成功，菜单ID：${res.data}`)
            this.dialogVisible = false
            this.loadMenuList()
            this.$bus.$emit('menu-updated')
            if (!this.form.id) {
              // 新增后提示同步
              this.$notify({
                title: '新增成功',
                message: `菜单「${params.menuName}」已创建，请在菜单列表中点击「同步」按钮，将其同步到各机构。`,
                type: 'success',
                duration: 6000
              })
            }
          } else {
            this.$message.error(res.msg || '操作失败')
          }
        } catch (e) {
          this.$message.error('操作异常')
        } finally {
          this.saving = false
        }
      })
    },

    /**
     * 重置表单
     */
    resetForm() {
      this.$refs.menuForm && this.$refs.menuForm.clearValidate()
    }
  }
}
</script>

<style scoped>
#menu-manage {
  padding: 16px;
}
.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
/* 表格列不换行，超长内容省略并悬停显示完整 */
#menu-manage >>> .el-table .cell {
  white-space: nowrap;
}
</style>
